// InterviewScheduler.jsx
// Employer: create interview slots for an applicant
// Student: view + confirm/decline their interview invite
// Props: applicationId, currentUser, otherParty, supabase, T, notify

import { useState, useEffect } from "react";

export function InterviewSchedulerEmployer({ applicationId, student, company, currentUser, supabase, T, notify }) {
  const [slots, setSlots]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({
    slot_time: "", duration_mins: "30", format: "video",
    meeting_link: "", location: "", notes: "",
  });
  const [saving, setSaving]   = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const { data } = await supabase.from("interview_slots")
      .select("*").eq("application_id", applicationId)
      .order("slot_time", { ascending: true });
    setSlots(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [applicationId]);

  const save = async () => {
    if (!form.slot_time) { notify("Please set a date and time.", false); return; }
    setSaving(true);
    const { error } = await supabase.from("interview_slots").insert({
      application_id: applicationId,
      employer_id:    currentUser.id,
      student_id:     student.id,
      slot_time:      new Date(form.slot_time).toISOString(),
      duration_mins:  parseInt(form.duration_mins),
      format:         form.format,
      meeting_link:   form.meeting_link || null,
      location:       form.location || null,
      notes:          form.notes || null,
      status:         "pending",
    });
    setSaving(false);
    if (error) { notify("Failed: " + error.message, false); return; }
    notify("Interview invite sent! ✓");
    setShowForm(false);
    setForm({ slot_time: "", duration_mins: "30", format: "video", meeting_link: "", location: "", notes: "" });
    load();
    // Update application status to interview
    await supabase.from("applications").update({ status: "interview" }).eq("id", applicationId);
  };

  const cancel = async (slotId) => {
    await supabase.from("interview_slots").update({ status: "cancelled", cancelled_at: new Date().toISOString(), cancelled_by: currentUser.id }).eq("id", slotId);
    notify("Interview cancelled.");
    load();
  };

  const FORMAT_ICONS = { video: "📹", phone: "📞", onsite: "🏢" };
  const STATUS_STYLES = {
    pending:     { bg: "#fef3c7", color: "#d97706", label: "⏳ Awaiting student" },
    confirmed:   { bg: "#dcfce7", color: "#16a34a", label: "✅ Confirmed" },
    cancelled:   { bg: "#fee2e2", color: "#dc2626", label: "❌ Cancelled" },
    completed:   { bg: "#e0e7ff", color: "#4338ca", label: "🏁 Completed" },
    rescheduled: { bg: "#fef3c7", color: "#d97706", label: "🔄 Rescheduled" },
  };

  const PHTime = (ts) => new Date(ts).toLocaleString("en-PH", {
    timeZone: "Asia/Manila", weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>🗓️ Interview Schedule</div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? "Cancel" : "+ Schedule Interview"}
        </button>
      </div>

      {/* Schedule form */}
      {showForm && (
        <div style={{ background: T.surface, borderRadius: 12, padding: 18, marginBottom: 16, border: `1.5px solid ${T.primary}30` }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Date & Time (Philippine Time)</label>
              <input type="datetime-local" className="input" value={form.slot_time}
                onChange={e => setForm(p => ({ ...p, slot_time: e.target.value }))}
                min={new Date().toISOString().slice(0, 16)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Duration</label>
              <select className="input" value={form.duration_mins} onChange={e => setForm(p => ({ ...p, duration_mins: e.target.value }))}>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Format</label>
              <select className="input" value={form.format} onChange={e => setForm(p => ({ ...p, format: e.target.value }))}>
                <option value="video">📹 Video Call</option>
                <option value="phone">📞 Phone Call</option>
                <option value="onsite">🏢 On-site</option>
              </select>
            </div>
            {form.format === "video" && (
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Meeting Link</label>
                <input className="input" placeholder="https://meet.google.com/... or Zoom link"
                  value={form.meeting_link} onChange={e => setForm(p => ({ ...p, meeting_link: e.target.value }))} />
              </div>
            )}
            {form.format === "onsite" && (
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Location / Address</label>
                <input className="input" placeholder="Office address or floor/room number"
                  value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} />
              </div>
            )}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.inkM, display: "block", marginBottom: 4 }}>Notes to Applicant (optional)</label>
              <textarea className="input" rows={2} placeholder="e.g. Please prepare to discuss your experience with React and show any past projects."
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving} style={{ flex: 2 }}>
              {saving ? "Sending…" : "Send Interview Invite →"}
            </button>
          </div>
        </div>
      )}

      {/* Existing slots */}
      {loading ? (
        <div style={{ color: T.inkL, fontSize: 13, textAlign: "center", padding: 20 }}>Loading…</div>
      ) : slots.length === 0 ? (
        <div style={{ color: T.inkL, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No interviews scheduled yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {slots.map(slot => {
            const ss = STATUS_STYLES[slot.status] || STATUS_STYLES.pending;
            return (
              <div key={slot.id} style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
                      {FORMAT_ICONS[slot.format]} {PHTime(slot.slot_time)}
                    </div>
                    <div style={{ fontSize: 12, color: T.inkL }}>{slot.duration_mins} min · {slot.format}</div>
                    {slot.meeting_link && (
                      <a href={slot.meeting_link} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: T.blue, display: "block", marginTop: 4 }}>🔗 Meeting link</a>
                    )}
                    {slot.location && <div style={{ fontSize: 12, color: T.inkL, marginTop: 2 }}>📍 {slot.location}</div>}
                    {slot.notes && <div style={{ fontSize: 12, color: T.inkM, marginTop: 4, fontStyle: "italic" }}>"{slot.notes}"</div>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, background: ss.bg, color: ss.color, padding: "3px 10px", borderRadius: 100 }}>{ss.label}</span>
                    {["pending", "confirmed"].includes(slot.status) && (
                      <button className="btn btn-ghost btn-xs" onClick={() => cancel(slot.id)}
                        style={{ color: T.red, borderColor: T.red, fontSize: 11 }}>Cancel</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function InterviewSchedulerStudent({ applicationId, currentUser, supabase, T, notify }) {
  const [slots, setSlots]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(null);

  const load = async () => {
    const { data } = await supabase.from("interview_slots")
      .select("*, employer:profiles!interview_slots_employer_id_fkey(full_name, avatar_url)")
      .eq("application_id", applicationId).eq("student_id", currentUser.id)
      .order("slot_time", { ascending: true });
    setSlots(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [applicationId]);

  const respond = async (slotId, action) => {
    setActing(slotId);
    const update = action === "confirm"
      ? { status: "confirmed", confirmed_at: new Date().toISOString() }
      : { status: "cancelled", cancelled_at: new Date().toISOString(), cancelled_by: currentUser.id };
    await supabase.from("interview_slots").update(update).eq("id", slotId);
    notify(action === "confirm" ? "Interview confirmed! ✓" : "Interview declined.");
    setActing(null);
    load();
  };

  const PHTime = (ts) => new Date(ts).toLocaleString("en-PH", {
    timeZone: "Asia/Manila", weekday: "long", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  const addToCalendar = (slot) => {
    const start = new Date(slot.slot_time);
    const end   = new Date(start.getTime() + slot.duration_mins * 60000);
    const fmt   = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const title = encodeURIComponent("OJT Interview");
    const desc  = encodeURIComponent((slot.notes || "") + (slot.meeting_link ? "\n\nMeeting: " + slot.meeting_link : ""));
    const loc   = encodeURIComponent(slot.meeting_link || slot.location || "");
    window.open(`https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${desc}&location=${loc}`, "_blank");
  };

  if (loading) return <div style={{ color: T.inkL, fontSize: 13, textAlign: "center", padding: 20 }}>Loading…</div>;
  if (slots.length === 0) return (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🗓️</div>
      <div style={{ fontSize: 13, color: T.inkL }}>No interview scheduled yet. Check back after the employer reviews your application.</div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {slots.map(slot => {
        const isUpcoming = new Date(slot.slot_time) > new Date();
        const STATUS_CONFIG = {
          pending:   { bg: "#fef3c7", color: "#d97706", label: "⏳ Action required", urgent: true },
          confirmed: { bg: "#dcfce7", color: "#16a34a", label: "✅ Confirmed" },
          cancelled: { bg: "#fee2e2", color: "#dc2626", label: "❌ Cancelled" },
          completed: { bg: "#e0e7ff", color: "#4338ca", label: "🏁 Completed" },
        };
        const sc = STATUS_CONFIG[slot.status] || STATUS_CONFIG.pending;
        return (
          <div key={slot.id} style={{
            background: "#fff", border: `2px solid ${sc.urgent && slot.status === "pending" ? "#fbbf24" : T.border}`,
            borderRadius: 14, padding: "16px 18px",
            boxShadow: sc.urgent && slot.status === "pending" ? "0 4px 20px rgba(245,166,35,.15)" : "none",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
                  {slot.format === "video" ? "📹" : slot.format === "phone" ? "📞" : "🏢"} Interview Invite
                </div>
                <div style={{ fontSize: 12, color: T.inkL }}>
                  From {slot.employer?.full_name || "Employer"} · {slot.duration_mins} min {slot.format} interview
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, padding: "3px 10px", borderRadius: 100 }}>{sc.label}</span>
            </div>

            <div style={{ background: T.surface, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: T.ink, marginBottom: 2 }}>
                {PHTime(slot.slot_time)}
              </div>
              {slot.meeting_link && (
                <a href={slot.meeting_link} target="_blank" rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: T.blue, marginTop: 6 }}>
                  🔗 Join meeting link
                </a>
              )}
              {slot.location && <div style={{ fontSize: 12, color: T.inkM, marginTop: 4 }}>📍 {slot.location}</div>}
            </div>

            {slot.notes && (
              <div style={{ background: T.blueL, borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 13, color: T.inkM }}>
                💬 <em>{slot.notes}</em>
              </div>
            )}

            {slot.status === "pending" && (
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-ghost" onClick={() => respond(slot.id, "decline")} disabled={acting === slot.id}
                  style={{ flex: 1, color: T.red, borderColor: T.red }}>
                  Decline
                </button>
                <button className="btn btn-primary" onClick={() => respond(slot.id, "confirm")} disabled={acting === slot.id}
                  style={{ flex: 2 }}>
                  {acting === slot.id ? "Confirming…" : "✓ Confirm Interview"}
                </button>
              </div>
            )}
            {slot.status === "confirmed" && isUpcoming && (
              <button className="btn btn-ghost" onClick={() => addToCalendar(slot)} style={{ width: "100%" }}>
                📅 Add to Google Calendar
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
